import React from "react";
import { useTranslation } from "react-i18next";
import { openPaySheet } from "@/components/pay/PaySheet";
import { useResumeJob } from "@/components/pay/useResumeJob";
import type { TFunction } from "i18next";
import { useUserCredits, refreshUserCredits } from "@/hooks/useUserCredits";
import {
  publishStoryBrief, useStoryBriefEvents,
  publishStudioProgress, publishStudioResult,
  requestStoryBoardPatch, requestStoryCommit, requestStoryReplan,
} from "@/components/landing/director-studio/studioBus";
import type { StoryBoard } from "@/components/landing/director-studio/types";
import { ShareCard } from "@/features/reel/ShareCard";
import { takeReelHandoff } from "@/features/reel/handoff";
import SiteMarkSwitch from "@/components/pay/SiteMarkSwitch";
import { keepSiteMark } from "@/lib/site-mark";

// TikTok Story flow INSIDE the Director chat console — powered by our local
// ComfyUI/Kokoro/Pexels workflow (tmp/story_engine.py), never a third-party
// engine.
//
// The tool takes ONE input: your writing. So this asks for one thing.
//
//   write  -> paste the text, pick nothing else
//   plan   -> /api/web/story/brief plans a board (free, no GPU, no charge)
//   review -> the board lands on the STORYBOARD CANVAS, not in this chat. The
//             user edits/rejects it there and presses Generate on the frame.
//   render -> /api/web/story/generate renders exactly the approved board
//
// Deliberately absent: hook/scenes fields, transition picker, caption-style
// picker, aspect picker, photo upload. The engine has no such controls —
// captions are always word-by-word ASS and output is always 1080x1920 — so
// offering them would be asking the user to decide things that do nothing.

type Phase = "write" | "planning" | "review" | "working" | "done";

const C = {
  bg: "#05070b",
  card: "#101722",
  border: "#2a3648",
  cyan: "#38bdf8",
  pink: "#38bdf8",
  purple: "#0ea5e9",
  white: "#ffffff",
  offwhite: "#e2e8f0",
  muted: "#94a3b8",
  green: "#34d399",
  gold: "#e3b341",
  red: "#f87171",
};

function authHeaders(json = true): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem("auth_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

async function uploadFile(t: TFunction, file: File, type: string): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("type", type);
  const r = await fetch("/api/web/upload", { method: "POST", headers: authHeaders(false), body: fd });
  const j = await r.json();
  if (!r.ok || !j.url) throw new Error(`${file.name}: ${j.message || j.error || t("story.uploadFailed")}`);
  return j.url as string;
}

/**
 * Fetch the finished video and hand back a blob: URL.
 *
 * /api/web/story/media is behind authenticateToken, and a <video src="…"> tag
 * cannot send an Authorization header — so pointing the tag straight at the
 * route 401'd and the finished render was invisible even though the mp4 existed
 * on disk. Fetch it with the token, then play it from a blob (CSP mediaSrc
 * already allows blob:). The alternative — putting the JWT in the URL — would
 * leak a credential into history, logs and referrers.
 */
async function fetchVideoBlobUrl(t: TFunction, path: string): Promise<string> {
  const r = await fetch(`/api/web/story/media?path=${encodeURIComponent(path)}`, { headers: authHeaders(false) });
  if (!r.ok) throw new Error(t("story.videoLoadFailed", { status: r.status }));
  return URL.createObjectURL(await r.blob());
}

// CapCut answers a finished brief with a row of one-tap chips ("Start
// generation", "Change to a female narrator", "Make the mood darker") instead of
// making you go hunt for the control. Same idea here — but only for knobs the
// engine honours. Voices are the real Kokoro ids; tones are the three the
// storyboard prompt emits.
function QuickActions() {
  const { t } = useTranslation();
  const chip: React.CSSProperties = {
    background: "rgba(255,255,255,.06)",
    border: `1px solid ${C.border}`,
    borderRadius: 999,
    color: C.offwhite,
    cursor: "pointer",
    fontSize: 13,
    padding: "6px 12px",
    whiteSpace: "nowrap",
  };
  // `id` is the stable test/analytics handle; `label` follows the toggle.
  const actions: Array<{ id: string; label: string; run: () => void; primary?: boolean }> = [
    { id: "start-generation", label: t("story.chips.start"), run: () => requestStoryCommit(), primary: true },
    { id: "female-narrator", label: t("story.chips.female"), run: () => requestStoryBoardPatch({ voice: "af_heart" }) },
    { id: "male-narrator", label: t("story.chips.male"), run: () => requestStoryBoardPatch({ voice: "am_michael" }) },
    { id: "viral", label: t("story.chips.viral"), run: () => requestStoryBoardPatch({ tone: "viral" }) },
    { id: "educational", label: t("story.chips.educational"), run: () => requestStoryBoardPatch({ tone: "educational" }) },
    { id: "plan-again", label: t("story.chips.replan"), run: () => requestStoryReplan() },
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {actions.map((a) => (
        <button
          key={a.id}
          type="button" data-testid={`story-chip-${a.id}`}
          onClick={a.run}
          style={a.primary
            ? { ...chip, background: `linear-gradient(135deg,${C.pink},${C.purple})`, border: "none", color: C.white, fontWeight: 700 }
            : chip}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

export default function StoryChatFlow() {
  const { t } = useTranslation();
  const [phase, setPhase] = React.useState<Phase>("write");
  const [text, setText] = React.useState("");
  // The story pasted in the Reel's "Make one like this" sheet (features/reel).
  const reelRef = React.useRef(false);
  React.useEffect(() => {
    if (reelRef.current) return;
    reelRef.current = true;
    const handoff = takeReelHandoff("story");
    if (handoff?.text) setText(handoff.text);
  }, []);
  const [note, setNote] = React.useState("");
  const [videoUrl, setVideoUrl] = React.useState("");
  const loggedIn = !!localStorage.getItem("auth_token");
  const { balance, serviceCosts } = useUserCredits(loggedIn);
  const runCost = serviceCosts?.story ?? null;
  const timers = React.useRef<number[]>([]);

  const ready = text.trim().length >= 40;

  // Revoke the blob when it's replaced or the component goes away — an
  // un-revoked object URL pins the whole video in memory for the session.
  const videoUrlRef = React.useRef("");
  React.useEffect(() => {
    const previous = videoUrlRef.current;
    if (previous && previous !== videoUrl) URL.revokeObjectURL(previous);
    videoUrlRef.current = videoUrl;
  }, [videoUrl]);

  React.useEffect(() => () => {
    timers.current.forEach((t) => clearInterval(t));
    if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
    publishStoryBrief(null); // never leave a stale board behind on unmount
    publishStudioProgress("story", null);
    publishStudioResult("story", null);
  }, []);

  // Every new phase supersedes the last, so kill any poll still in flight —
  // otherwise a re-plan races its predecessor and the loser's response
  // overwrites the winner's board.
  const track = (id: number) => { timers.current.push(id); return id; };
  const stopPolling = () => {
    timers.current.forEach((t) => clearInterval(t));
    timers.current = [];
  };

  // ---- Phase 1: plan a board (free) ----
  const plan = React.useCallback(async (source: string) => {
    stopPolling();
    setPhase("planning");
    setNote(t("story.planning"));
    publishStoryBrief(null);
    // Planning is a job in flight — it lives on the canvas as a loading card.
    publishStudioProgress("story", { title: t("story.planningTitle"), message: t("story.planning"), progress: null });
    try {
      const r = await fetch("/api/web/story/brief", {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ text: source }),
      }).then((res) => res.json());
      if (!r.jobId) {
        setPhase("write");
        setNote(`${r.message || t("story.couldNotPlan")}`);
        publishStudioProgress("story", null);
        return;
      }
      // The brief is planned on the server now and answers the board at once; the
      // poll below is only for a service that answers an id first (and the status
      // of that id is known only to the instance that planned it, so never wait on
      // it when the board is already here).
      if (r.status === "completed" && r.board) {
        publishStudioProgress("story", null);
        publishStoryBrief({
          board: r.board as StoryBoard,
          estimatedSeconds: typeof r.estimated_seconds === "number" ? r.estimated_seconds : null,
          renderCostCredits: runCost,
        });
        setPhase("review");
        setNote("");
        return;
      }
      const poll = track(window.setInterval(async () => {
        try {
          const s = await fetch(`/api/web/story/brief/status?id=${r.jobId}`, { headers: authHeaders() })
            .then((res) => res.json());
          if (s.status === "completed" && s.board) {
            clearInterval(poll);
            publishStudioProgress("story", null);
            publishStoryBrief({
              board: s.board as StoryBoard,
              estimatedSeconds: s.estimated_seconds ?? null,
              renderCostCredits: runCost,
            });
            setPhase("review");
            setNote("");
            return;
          }
          if (s.status === "failed") {
            clearInterval(poll);
            publishStudioProgress("story", null);
            setPhase("write");
            setNote(`${s.error || t("story.planningFailed")}`);
            return;
          }
          if (s.message) {
            setNote(s.message);
            publishStudioProgress("story", { title: t("story.planningTitle"), message: s.message, progress: null });
          }
        } catch { /* keep polling */ }
      }, 2000));
    } catch (e: unknown) {
      setPhase("write");
      setNote(`${e instanceof Error ? e.message : String(e)}`);
      publishStudioProgress("story", null);
    }
  }, [runCost]);

  // ---- Phase 2: render the board the user approved on the canvas ----
  const render = React.useCallback(async (board: StoryBoard, music: File | null) => {
    stopPolling();
    setPhase("working");
    setNote(t("story.startingRender"));
    publishStoryBrief({ board, estimatedSeconds: null, renderCostCredits: runCost, rendering: true });
    publishStudioResult("story", null); // a new render supersedes the last player node
    publishStudioProgress("story", { title: t("story.renderingTitle"), message: t("story.startingRender"), progress: null });
    try {
      let musicUrl: string | null = null;
      if (music) {
        setNote(t("story.uploadingMusic"));
        musicUrl = await uploadFile(t, music, "audio");
      }
      const r = await fetch("/api/web/story/generate", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ board, music_url: musicUrl, watermark: keepSiteMark() }),
      }).then((res) => res.json());
      if (!r.jobId) {
        setPhase("review");
        publishStoryBrief({ board, estimatedSeconds: null, renderCostCredits: runCost });
        publishStudioProgress("story", null);
        setNote(`${r.message || (r.error === "insufficient_credits" ? t("story.insufficientCredits") : t("story.couldNotStartRender"))}`);
        return;
      }
      refreshUserCredits(); // charged at launch
      setNote(t("story.renderStarted", { count: board.beats.length }));
      const poll = track(window.setInterval(async () => {
        try {
          const s = await fetch(`/api/web/story/status?id=${r.jobId}`, { headers: authHeaders() })
            .then((res) => res.json());
          if (s.status === "completed") {
            clearInterval(poll);
            setPhase("done");
            publishStoryBrief(null);
            publishStudioProgress("story", null);
            refreshUserCredits();
            if (s.result_url) {
              setNote(t("story.loadingVideo"));
              // The player NODE resolves the API path itself (blob-cached, never
              // revoked) — don't hand it this component's revocable blob URL.
              publishStudioResult("story", {
                title: t("story.resultTitle"),
                url: `/api/web/story/media?path=${encodeURIComponent(s.result_url)}`,
              });
              try {
                setVideoUrl(await fetchVideoBlobUrl(t, s.result_url));
                setNote(t("story.videoReady"));
              } catch (e: unknown) {
                // The render SUCCEEDED — say so, and don't imply it's lost.
                setNote(t("story.previewFailed", { reason: e instanceof Error ? e.message : String(e) }));
              }
            } else {
              setNote(t("story.savedToGallery"));
            }
            return;
          }
          if (s.status === "failed") {
            clearInterval(poll);
            setNote(`${s.error || t("story.renderFailed")} ${t("story.renderFailedTail")}`);
            setPhase("review");
            publishStoryBrief({ board, estimatedSeconds: null, renderCostCredits: runCost });
            publishStudioProgress("story", null);
            refreshUserCredits();
            return;
          }
          setNote(s.message || t("story.renderingNote"));
          publishStudioProgress("story", {
            title: t("story.renderingTitle"),
            message: s.message || t("story.renderingNote"),
            progress: Number.isFinite(s.progress) && s.progress > 0 ? s.progress : null,
          });
        } catch { /* keep polling */ }
      }, 3500));
    } catch (e: unknown) {
      setPhase("review");
      publishStoryBrief({ board, estimatedSeconds: null, renderCostCredits: runCost });
      publishStudioProgress("story", null);
      setNote(`${e instanceof Error ? e.message : String(e)}`);
    }
  }, [runCost]);

  // BACK FROM CHECKOUT (Studio Blueprint 04): the board the card held when
  // the buyer paid comes back and, once this flow's balance shows the new
  // credits, the same story starts. A music file cannot ride through
  // checkout, so a board that had one comes back to have it added again.
  const [resumeBoard, setResumeBoard] = React.useState<StoryBoard | null>(null);
  const putBack = (board: StoryBoard) => {
    publishStoryBrief({ board, estimatedSeconds: null, renderCostCredits: runCost });
    setPhase("review");
  };
  useResumeJob("tiktok", loggedIn && runCost !== null && balance !== null, {
    start: (job) => {
      const p = job.payload as { board?: StoryBoard; hadMusic?: boolean } | undefined;
      if (!p?.board) return;
      if (p.hadMusic) {
        putBack(p.board);
        setNote(t("story.addMusicAgain"));
        return;
      }
      setResumeBoard(p.board);
    },
    restore: (job) => {
      const p = job.payload as { board?: StoryBoard } | undefined;
      if (p?.board) putBack(p.board);
    },
  });
  React.useEffect(() => {
    if (!resumeBoard || balance === null || runCost === null || balance < runCost) return;
    setResumeBoard(null);
    void render(resumeBoard, null);
  }, [resumeBoard, balance, runCost, render]);

  // The canvas card is where the user acts; those intents land back here
  // because this is the one place that touches the API and their credits.
  useStoryBriefEvents({
    onRender: render,
    onReplan: () => { if (text.trim()) void plan(text.trim()); },
    onDiscard: () => {
      stopPolling();
      publishStoryBrief(null);
      publishStudioProgress("story", null);
      setPhase("write");
      setNote(t("story.boardDiscarded"));
    },
  });

  if (!loggedIn) {
    return (
      <div className="text-[15px] leading-[1.55] sm:text-sm">
        <p className="mb-3">{t("story.signInPrompt")}</p>
        {/* Back to this stop after signing in: a bare / opens on the welcome
            cards now (2026-09-24), not the studio this link was pressed in.
            44px tall and 15px on a phone (was 34px): it is the only way on.
            Full width below sm like the pitch CTAs, and balanced if a long
            label (Spanish at 320px) has to take two lines. 4px sides
            below 360px (2026-09-25) keep "Registrarse / Iniciar sesión →"
            on one line at 320. */}
        <a href="/signup?redirect_to=%2F%3Ftool%3Dstory" className="inline-flex min-h-[44px] w-full items-center justify-center text-balance rounded-md bg-primary px-5 py-2.5 text-center text-[15px] font-semibold text-primary-foreground active:scale-[0.98] max-[359px]:px-1 sm:w-auto sm:text-sm">{t("story.signUpLogIn")}</a>
      </div>
    );
  }

  return (
    <div data-testid="story-flow" style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: "system-ui, sans-serif", fontSize: 13, color: C.offwhite }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 3, background: `linear-gradient(90deg,${C.cyan},${C.pink})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t("story.title")}
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${C.border}`, borderRadius: 999, padding: "4px 10px", background: "rgba(227,179,65,.08)" }}>
          <span style={{ fontSize: 13 }}></span>
          <span style={{ fontSize: 13, fontWeight: 800, color: C.gold, fontFamily: "'Rajdhani', sans-serif" }}>
            {balance === null ? "—" : balance.toLocaleString()}{runCost !== null ? t("story.runCost", { cost: runCost.toLocaleString() }) : ""}
          </span>
        </div>
      </div>

      {phase === "write" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, color: C.offwhite, lineHeight: 1.6 }}>
            <b style={{ color: C.white }}>{t("story.pasteLead")}</b> {t("story.pasteBody")}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={7}
            data-testid="story-text" placeholder={t("story.pastePlaceholder")} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, color: C.offwhite, borderRadius: 8, padding: "10px 12px", fontSize: 13, lineHeight: 1.6, resize: "vertical", boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => void plan(text.trim())}
              disabled={!ready}
              data-testid="story-plan" style={{ borderRadius: 10, border: "none", background: ready ? `linear-gradient(135deg,${C.pink},${C.purple})` : "rgba(255,255,255,.08)", color: ready ? C.white : C.muted, fontWeight: 700, letterSpacing: 1, padding: "10px 18px", fontSize: 12, cursor: ready ? "pointer" : "default", fontFamily: "'Rajdhani', sans-serif" }}
            >{t("story.planMyVideo")}
            </button>
            <span style={{ fontSize: 12, color: C.muted }}>
              {ready ? t("story.freeApprove") : t("story.pasteToStart")}
            </span>
          </div>
          {note && <div style={{ fontSize: 13, color: C.offwhite }}>{note}</div>}
        </div>
      )}

      {phase === "planning" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
          <div style={{ color: C.muted, fontSize: 12 }}>{note || t("story.planningShort")}</div>
          <div style={{ height: 6, borderRadius: 3, background: C.bg, overflow: "hidden", marginTop: 8 }}>
            <div style={{ height: "100%", width: "50%", background: `linear-gradient(90deg,${C.cyan},${C.pink})`, animation: "ldc-shimmer 1.2s ease-in-out infinite" }} />
          </div>
        </div>
      )}

      {phase === "review" && (
        <div data-testid="story-review-pointer" style={{ background: C.card, border: `1px solid ${C.cyan}`, borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, color: C.offwhite, lineHeight: 1.6 }}>{t("story.reviewLead")}
            <b style={{ color: C.white }}> {t("story.reviewBold")}</b>
          </div>
          <div style={{ fontSize: 13, color: C.muted }}>{t("story.nothingCharged")}
            {runCost !== null ? <> {t("story.renderWillCost")} <b style={{ color: C.gold }}>{t("story.renderCostCredits", { count: runCost.toLocaleString() })}</b></> : null}.
          </div>
          <SiteMarkSwitch testId="story-site-mark" style={{ color: C.offwhite }} />
          {balance !== null && runCost !== null && balance < runCost && (
            <div style={{ fontSize: 13, color: C.red }}>{t("story.balanceTooLow", { balance: balance.toLocaleString() })}{" "}
              <button type="button" data-testid="story-buy" onClick={() => openPaySheet({ need: runCost, have: balance, label: t("story.payForRender") })}
                style={{ color: C.gold, fontWeight: 700, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>{t("story.buyCredits")}</button>
            </div>
          )}
          {/* Quick actions, CapCut-style. Every chip maps to something the
              engine really consumes — narrator, tone, or the run itself. There
              is no "change the aspect ratio" chip because the engine can't. */}
          <QuickActions />
          {note && <div style={{ fontSize: 13, color: C.offwhite }}>{note}</div>}
        </div>
      )}

      {phase === "working" && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
          <div style={{ color: C.muted, fontSize: 12 }}>{note || t("story.renderingShort")}</div>
          <div style={{ height: 6, borderRadius: 3, background: C.bg, overflow: "hidden", marginTop: 8 }}>
            <div style={{ height: "100%", width: "50%", background: `linear-gradient(90deg,${C.cyan},${C.pink})`, animation: "ldc-shimmer 1.2s ease-in-out infinite" }} />
          </div>
        </div>
      )}

      {phase === "done" && (
        <div style={{ background: C.card, border: `1px solid rgba(52,211,153,.35)`, borderRadius: 12, padding: 12, display: "grid", gap: 8 }}>
          <div>{note}</div>
          {videoUrl && <video src={videoUrl} controls playsInline style={{ width: "100%", maxWidth: 300, borderRadius: 10, border: `1px solid ${C.border}` }} />}
          {/* The link back to this studio, with the sharer's referral code. */}
          <ShareCard studio="story" mediaUrl={videoUrl || undefined} fileName="story-video.mp4" />
          <button
            onClick={() => { setPhase("write"); setVideoUrl(""); setNote(""); }}
            style={{ justifySelf: "start", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.offwhite, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
          >{t("story.makeAnother")}
          </button>
        </div>
      )}
    </div>
  );
}
